export type Direction = 'up' | 'down' | 'left' | 'right';
export type WorldState = 'normal' | 'overdrive';
export type Difficulty = 'relaxed' | 'standard' | 'expert';
export type CharacterId = 'mox' | 'vela';
export type FlavorId = 'vanilla' | 'strawberry' | 'greenApple' | 'chocolate';
export type FruitKind =
  | 'strawberry'
  | 'greenApple'
  | 'orange'
  | 'pear'
  | 'cherries'
  | 'grapes'
  | 'watermelon'
  | 'pineapple'
  | 'banana'
  | 'blueberries';
export type FruitBehavior = 'stationary' | 'slow-roam' | 'leaf-flight' | 'teleport' | 'runner';
export type FruitPhase =
  | 'idle'
  | 'drift-warning'
  | 'drifting'
  | 'flight-warning'
  | 'airborne'
  | 'landing'
  | 'teleport-warning'
  | 'teleporting'
  | 'run-warning'
  | 'running'
  /** Sealed inside a Frostwall; uncollectible until the ice is melted. */
  | 'frozen';
export type EnemyKind = 'tracer' | 'rammer' | 'seer' | 'wisp' | 'scoop';
/**
 * Cherry Scoop lane-charge cycle. `ready` patrols, `locking` freezes on the
 * spotted lane while the telegraph plays, `sprinting` is the eight-second
 * charge, and `winded` is the cooldown where it runs at its normal speed and
 * cannot lock on again.
 */
export type LaneSprintPhase = 'ready' | 'locking' | 'sprinting' | 'winded';
export type Rank = 'C' | 'B' | 'A' | 'S';

export interface Cell {
  readonly x: number;
  readonly y: number;
}

export interface MutableCell {
  x: number;
  y: number;
}

export interface InputFrame {
  direction: Direction | null;
  placeWall: boolean;
  shatter: boolean;
  special: boolean;
}

export interface PlayerState {
  id: 1 | 2;
  character: CharacterId;
  flavor: FlavorId;
  cell: MutableCell;
  from: MutableCell;
  target: MutableCell;
  progress: number;
  facing: Direction;
  buffered: Direction | null;
  bufferTicks: number;
  moving: boolean;
  health: number;
  maxHealth: number;
  invulnerableTicks: number;
  specialCooldownTicks: number;
  downed: boolean;
  reviveTicks: number;
  decoy: DecoyState | null;
  wallToggleQueued: boolean;
  /**
   * Whether the build key was already down last tick. The Frostwall action is
   * edge-triggered: a held key must perform exactly one build or melt, not one
   * per tick, or a single human keypress toggles the same wall several times.
   */
  wallHeld: boolean;
}

export interface DecoyState {
  cell: MutableCell;
  direction: Direction;
  remainingTicks: number;
  moveTicks: number;
}

/** Per-enemy lane-charge state. Only kinds with the ability carry one. */
export interface LaneSprintState {
  phase: LaneSprintPhase;
  /** Fixed steps left in the current phase. Ignored while `ready`. */
  ticksRemaining: number;
  /** Lane the charge is committed to, or null when chasing freely. */
  lane: Direction | null;
}

export interface EnemyState {
  id: number;
  kind: EnemyKind;
  cell: MutableCell;
  from: MutableCell;
  target: MutableCell;
  progress: number;
  moving: boolean;
  facing: Direction;
  stunnedTicks: number;
  alertTicks: number;
  path: Cell[];
  pathGoal: Cell | null;
  topologyRevision: number;
  predicted: Cell | null;
  warningTicks: number;
  sprint: LaneSprintState | null;
  /**
   * Furthest room this hunter may ever occupy. It can chase a cat back through
   * every earlier room, but never follow one forward past its own.
   */
  homeRoom: number;
}

export interface FlipwallLine {
  id: number;
  owner: 1 | 2;
  cells: Cell[];
  createdTick: number;
  expiresTick: number;
  warningTick: number;
  growTicks: number;
}

/**
 * Deterministic fruit actor state shared by the simulation and renderer.
 * `cell` is the fruit's current collectible/reserved grid cell. During travel,
 * presentation interpolates from `from` to `target` using `progress`.
 */
export interface FruitActorState {
  readonly id: number;
  readonly kind: FruitKind;
  readonly behavior: FruitBehavior;
  cell: MutableCell;
  from: MutableCell;
  target: MutableCell;
  facing: Direction;
  phase: FruitPhase;
  phaseTicksRemaining: number;
  phaseDurationTicks: number;
  progress: number;
  collectible: boolean;
  nextActionTick: number;
  /**
   * True while a Frostwall covers this fruit. Frozen fruit hold still, cannot
   * be picked up, and are drawn as a silhouette inside the ice.
   */
  frozen: boolean;
}

export interface ScoreState {
  score: number;
  combo: number;
  comboTicks: number;
  maxCombo: number;
  damageTaken: number;
  stunCount: number;
  nearMisses: number;
  shardFound: boolean;
  extractionTicks: number;
}

export interface SimulationEvent {
  type:
    | 'move'
    | 'core'
    | 'final-core'
    | 'fruit-wave'
    | 'fruit-warning'
    | 'fruit-move'
    | 'fruit-teleport'
    | 'wall-place'
    | 'wall-reject'
    | 'wall-crack'
    | 'wall-shatter'
    | 'enemy-alert'
    | 'enemy-stun'
    | 'shift-warning'
    | 'shift'
    | 'portal-open'
    | 'damage'
    | 'thaw'
    | 'revive'
    | 'special'
    | 'shard'
    | 'boss-phase'
    | 'victory'
    | 'failure';
  cell?: Cell;
  playerId?: 1 | 2;
  amount?: number;
  label?: string;
  fruitKind?: FruitKind;
  enemyKind?: EnemyKind;
}

export interface RunResult {
  levelId: number;
  timeMs: number;
  score: number;
  rank: Rank;
  speedMedal: boolean;
  noDamageMedal: boolean;
  shardMedal: boolean;
  maxCombo: number;
}

export const DIRECTIONS: readonly Direction[] = ['up', 'down', 'left', 'right'];

export const DIRECTION_VECTORS: Readonly<Record<Direction, Cell>> = {
  up: { x: 0, y: -1 },
  down: { x: 0, y: 1 },
  left: { x: -1, y: 0 },
  right: { x: 1, y: 0 },
};

export function cellKey(cell: Cell): string {
  return `${cell.x},${cell.y}`;
}

export function sameCell(a: Cell, b: Cell): boolean {
  return a.x === b.x && a.y === b.y;
}

export function manhattan(a: Cell, b: Cell): number {
  return Math.abs(a.x - b.x) + Math.abs(a.y - b.y);
}

export function stepCell(cell: Cell, direction: Direction): Cell {
  const delta = DIRECTION_VECTORS[direction];
  return { x: cell.x + delta.x, y: cell.y + delta.y };
}

export function directionBetween(from: Cell, to: Cell): Direction {
  if (to.x > from.x) return 'right';
  if (to.x < from.x) return 'left';
  if (to.y > from.y) return 'down';
  return 'up';
}
